// Écran « Impact » : jusqu'à trois variantes d'architecture, une demande à l'API par variante. Chaque chiffre affiché est
// celui de la réponse (moteur déterministe) : aucune soustraction, somme ni comparaison n'est faite dans le navigateur,
// et la différence est celle que l'API rend, même si le lecteur la referait autrement. Vocabulaire : docs/REGLE-EDITORIALE.md.
import type { Carte, EffetApplication } from '../api/types';
import { Etiquette } from '../composants/communs';
import { ecartFourchetteVue, expliquerRefusApplication, fourchetteVue, MAX_VARIANTES, nomObjet, pluriel } from '../domaine/applicationCible';
import { decimal, eurosLong, fourchette, LIBELLE_STATUT_NIS2, pourcent } from '../domaine/format';
import { lienVers } from '../routeur';
import { type VarianteApplication } from './Application';

const FLECHE = ' → ';

function pct(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : pourcent(v);
}
function nis2(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${decimal(v)} / 10`;
}
function noms(ids: string[] | undefined, v: VarianteApplication, carte: Carte | undefined): string {
  return (ids ?? []).map((id) => nomObjet(id, v.application, carte)).join(', ');
}

function Ligne({ libelle, children, id }: { libelle: string; children: React.ReactNode; id?: string }) {
  return (
    <div className="ligne-effet" data-testid={id}>
      <dt>{libelle}</dt>
      <dd className="num">{children}</dd>
    </div>
  );
}

function Resultat({ e, v, carte }: { e: EffetApplication; v: VarianteApplication; carte: Carte | undefined }) {
  const processus = noms(e.processus_dependants, v, carte);
  const composants = noms(e.composants_dependants, v, carte);
  const dependants = [processus && `processus : ${processus}`, composants && `composants : ${composants}`].filter(Boolean).join(' ; ');
  const nouvellesPortes = noms(e.portes_entree_nouvelles, v, carte);
  const pointsUniques = noms(e.points_uniques_nouveaux, v, carte);
  return (
    <>
      <dl className="effet">
        <Ligne libelle="Portes d’entrée faibles" id="portes">
          {e.portes_entree.avant}
          {FLECHE}
          {e.portes_entree.apres}
        </Ligne>
        <Ligne libelle="Arrêt maximal" id="arret">
          {pct(e.avant.rayon_max)}
          {FLECHE}
          {pct(e.apres.rayon_max)}
        </Ligne>
        <Ligne libelle="Pertes potentielles estimées par an" id="pertes">
          {fourchetteVue(e.avant.perte_annuelle_attendue, fourchette)}
          {FLECHE}
          {fourchetteVue(e.apres.perte_annuelle_attendue, fourchette)}
        </Ligne>
        <Ligne libelle="Écart des pertes potentielles estimées" id="ecart">
          {ecartFourchetteVue(e.difference.perte_annuelle_attendue)}
        </Ligne>
        <Ligne libelle="Indicateur de progression NIS2" id="nis2">
          {nis2(e.avant.score_nis2)}
          {FLECHE}
          {nis2(e.apres.score_nis2)}
        </Ligne>
        <Ligne libelle="Si l’application tombe" id="dependants">
          {dependants || 'aucun processus ni composant'}
        </Ligne>
      </dl>
      <p className="note" data-testid="note-nis2">Décompte des mesures pas encore fourni par l’API.</p>
      {nouvellesPortes && <p className="note">Portes d’entrée nouvelles : {nouvellesPortes}.</p>}
      {pointsUniques && <p className="note">Points uniques de défaillance nouveaux : {pointsUniques}.</p>}
      {(e.nis2 ?? []).length > 0 && (
        <ul className="liste-points" aria-label="Mesures NIS2 qui changent">
          {(e.nis2 ?? []).map((m) => (
            <li key={m.mesure}>
              {m.libelle} : {m.avant ? (LIBELLE_STATUT_NIS2[m.avant] ?? m.avant) : '—'}
              {FLECHE}
              {LIBELLE_STATUT_NIS2[m.apres] ?? m.apres}
            </li>
          ))}
        </ul>
      )}
      {(e.gestes ?? []).length > 0 && (
        <p className="note">
          Gestes du moteur que l’application rend utiles : {(e.gestes ?? []).map((g) => `${g.libelle ?? g.id}${g.nouveau ? ' (nouveau)' : ''}`).join(', ')}.
          {e.essai_gestes?.cout.total ? ` Coût estimé de ces gestes (moteur, hors coût de construction de l’application) : ${eurosLong(e.essai_gestes.cout.total.valeur)} ; pertes potentielles estimées après : ${fourchetteVue(e.essai_gestes.apres.perte_annuelle_attendue, fourchette)} par an.` : ''}
        </p>
      )}
      <p className="note">
        {pluriel(e.citations_verifiees, 'citation')} {e.citations_verifiees > 1 ? 'vérifiées' : 'vérifiée'} mot pour mot · calculé sur la révision {e.revision} de la carte · rien n’est écrit.
      </p>
    </>
  );
}

function Carte1({ v, carte, surCalcul, surEcart }: { v: VarianteApplication; carte: Carte | undefined; surCalcul: () => void; surEcart: () => void }) {
  const titre = `Variante ${v.lettre}`;
  return (
    <article className="panneau variante-app" aria-labelledby={`t-var-${v.cle}`} data-testid={`variante-${v.lettre}`}>
      <h3 id={`t-var-${v.cle}`}>
        {titre}
        <small className="note" style={{ display: 'block' }}>
          {v.application.nom}
        </small>
      </h3>
      {v.etat === 'attente' && <p className="muted">Effet pas encore calculé.</p>}
      {v.etat === 'calcul' && (
        <p className="chargement" role="status">
          Calcul par le moteur…
        </p>
      )}
      {v.etat === 'erreur' && (
        <div className="encadre erreur" role="alert">
          <p>
            <b>L’effet de la {titre.replace('Variante', 'variante')} n’a pas pu être calculé.</b>
          </p>
          <p>{expliquerRefusApplication(v.erreur)}</p>
        </div>
      )}
      {v.etat === 'ok' && v.effet && <Resultat e={v.effet} v={v} carte={carte} />}
      <div className="rangee-actions">
        <button type="button" className="btn petit principal" onClick={surCalcul} disabled={v.etat === 'calcul'}>
          {v.etat === 'ok' ? 'Recalculer' : 'Calculer l’effet'}
        </button>
        <button type="button" className="btn petit" onClick={surEcart}>
          Écarter de la comparaison
        </button>
      </div>
    </article>
  );
}

export function EcranImpact({ variantes, carte, surCalcul, surEcart }: { variantes: VarianteApplication[]; carte: Carte | undefined; surCalcul: (v: VarianteApplication) => void; surEcart: (cle: number) => void }) {
  if (variantes.length === 0)
    return (
      <p className="encadre info">
        Aucune application cible n’est chargée. <a href={lienVers('application')}>Chargez-en jusqu’à {MAX_VARIANTES} à l’écran « Besoin »</a>.
      </p>
    );
  const enCours = variantes.some((v) => v.etat === 'calcul');
  const aCalculer = variantes.filter((v) => v.etat === 'attente' || v.etat === 'erreur');
  return (
    <>
      <p className="sous-titre">
        Calculé par le moteur, sur la carte augmentée de l’application, pour chaque variante d’architecture proposée, à valider par
        l’entreprise. Aucun chiffre ne vient d’un modèle d’IA.
      </p>
      <p className="encadre provisoire" data-testid="mentions">
        <b>Pertes potentielles estimées, conditionnelles aux hypothèses retenues</b> (calibration provisoire, non validée par un
        actuaire). <b>Indicateur de progression sur les mesures évaluées, pas une certification de conformité.</b>
      </p>
      <div>
        <button type="button" className="btn principal" aria-describedby="etat-calcul" disabled={enCours || aCalculer.length === 0} onClick={() => aCalculer.forEach((v) => surCalcul(v))}>
          Calculer les variantes{aCalculer.length ? ` (${aCalculer.length})` : ''}
        </button>
        <p id="etat-calcul" className="note" data-testid="etat-calcul">
          {enCours
            ? 'Calcul en cours : le bouton se rouvre à la fin.'
            : aCalculer.length === 0
              ? 'Toutes les variantes sont calculées ; « Recalculer » refait la demande d’une variante.'
              : 'Une demande part à l’API par variante non calculée.'}
        </p>
      </div>
      <div className="grille g3">
        {variantes.map((v) => (
          <Carte1 key={v.cle} v={v} carte={carte} surCalcul={() => surCalcul(v)} surEcart={() => surEcart(v.cle)} />
        ))}
      </div>
      <p className="note">
        <Etiquette ton="neutre">Coût de construction</Etiquette> à chiffrer : ce n’est pas un chiffre du moteur ; il ne s’affichera
        que s’il vient d’un devis saisi. Le choix d’une variante appartient à l’entreprise : cet écran ne retient rien.
      </p>
    </>
  );
}
